import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { 
  Users, 
  Crown, 
  Star, 
  Trophy, 
  Ban, 
  CheckCircle, 
  Search,
  ArrowLeft,
  Settings,
  BarChart3,
  Edit,
  Eye,
  RefreshCw,
  Coins,
  Heart,
  Zap,
  Save,
  X,
  Newspaper,
  Plus,
  Trash2,
  Calendar
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminPage() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState("users");
  const [editField, setEditField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [adminToken, setAdminToken] = useState(null);
  
  // News state
  const [news, setNews] = useState([]);
  const [showNewsForm, setShowNewsForm] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [newsForm, setNewsForm] = useState({
    title: "",
    content: "",
    category: "announcement",
    is_published: true
  });
  
  // User creation state
  const [showUserForm, setShowUserForm] = useState(false);
  const [userForm, setUserForm] = useState({
    email: "",
    password: "",
    player_name: "",
    initial_stars: 0,
    initial_coins: 0,
    is_premium_member: false,
    battle_pass_premium: false
  });
  const [creatingUser, setCreatingUser] = useState(false);
  
  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [lastSyncResult, setLastSyncResult] = useState(null);
  
  // Sync function
  const handleSync = async () => {
    if (!adminToken) {
      toast.error("Please login again");
      return;
    }
    
    setSyncing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        setLastSyncResult(result);
        if (result.success) {
          toast.success(`Sync complete! ${result.leaderboard_synced} users synced to leaderboard`);
          // Refresh data after sync
          fetchData(adminEmail);
        } else {
          toast.error("Sync failed: " + result.error);
        }
      } else {
        toast.error("Sync request failed");
      }
    } catch (error) {
      toast.error("Sync error: " + error.message);
    } finally {
      setSyncing(false);
    }
  };
  
  // Fetch sync status
  const fetchSyncStatus = async () => {
    if (!adminToken) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/sync/status`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      if (response.ok) {
        const status = await response.json();
        setSyncStatus(status);
      }
    } catch (error) {
      console.error("Failed to fetch sync status:", error);
    }
  };
  
  // Fetch sync status when adminToken changes
  useEffect(() => {
    if (adminToken) {
      fetchSyncStatus();
    }
  }, [adminToken]);

  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.is_admin) {
          // Use token from admin login response
          if (data.token) {
            setAdminToken(data.token);
          }
          
          setIsLoggedIn(true);
          setAdminEmail(email);
          fetchData(email);
        } else {
          setLoginError("Not authorized as admin");
        }
      } else {
        const error = await response.json();
        setLoginError(error.detail || "Login failed");
      }
    } catch (error) {
      setLoginError("Connection error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch all data
  const fetchData = async (adminEmailParam) => {
    setLoading(true);
    try {
      // Fetch users
      const usersRes = await fetch(`${API_URL}/api/admin/users?admin_email=${adminEmailParam}`);
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData.users);
      }

      // Fetch stats
      const statsRes = await fetch(`${API_URL}/api/admin/stats/overview?admin_email=${adminEmailParam}`);
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
      
      // News will be fetched when adminToken is set via useEffect
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch news when adminToken changes
  useEffect(() => {
    if (adminToken) {
      fetchNews();
    }
  }, [adminToken]);

  // Fetch all news (including unpublished)
  const fetchNews = async () => {
    if (!adminToken) return;
    
    try {
      const newsRes = await fetch(`${API_URL}/api/admin/news`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      if (newsRes.ok) {
        const newsData = await newsRes.json();
        setNews(newsData);
      }
    } catch (error) {
      console.error("Failed to fetch news:", error);
    }
  };

  // Create or update news
  const handleSaveNews = async () => {
    if (!adminToken) {
      toast.error("Please login again");
      return;
    }
    
    try {
      const url = editingNews 
        ? `${API_URL}/api/admin/news/${editingNews.id}`
        : `${API_URL}/api/admin/news`;
      
      const response = await fetch(url, {
        method: editingNews ? 'PUT' : 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(newsForm)
      });
      
      if (response.ok) {
        toast.success(editingNews ? "News updated!" : "News created!");
        setShowNewsForm(false);
        setEditingNews(null);
        setNewsForm({ title: "", content: "", category: "announcement", is_published: true });
        fetchNews();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save news");
      }
    } catch (error) {
      toast.error("Failed to save news");
    }
  };

  // Delete news
  const handleDeleteNews = async (newsId) => {
    if (!confirm("Are you sure you want to delete this news article?")) return;
    if (!adminToken) {
      toast.error("Please login again");
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/news/${newsId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      if (response.ok) {
        toast.success("News deleted!");
        fetchNews();
      }
    } catch (error) {
      toast.error("Failed to delete news");
    }
  };

  // Edit news
  const handleEditNews = (newsItem) => {
    setEditingNews(newsItem);
    setNewsForm({
      title: newsItem.title,
      content: newsItem.content,
      category: newsItem.category || "announcement",
      is_published: newsItem.is_published
    });
    setShowNewsForm(true);
  };

  // Ban/unban user
  const handleBanUser = async (userId, ban) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/user/ban?admin_email=${adminEmail}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, banned: ban, reason: ban ? "Admin action" : null })
      });

      if (response.ok) {
        fetchData(adminEmail);
        setSelectedUser(null);
      }
    } catch (error) {
      console.error("Ban action failed:", error);
    }
  };

  // Adjust user stats
  const handleAdjustStats = async (userId, field, value) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/user/adjust?admin_email=${adminEmail}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, field, value: parseInt(value) })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || `Updated ${field} successfully!`);
        
        // Update selectedUser locally for immediate feedback
        if (selectedUser && selectedUser.id === userId) {
          setSelectedUser(prev => ({
            ...prev,
            stats: {
              ...prev.stats,
              [field]: parseInt(value),
              // Handle battle pass premium specially
              ...(field === "battle_pass_premium" ? { has_battle_pass: parseInt(value) === 1 } : {})
            }
          }));
        }
        
        fetchData(adminEmail);
        setEditField(null);
        setEditValue("");
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to update");
      }
    } catch (error) {
      console.error("Adjust failed:", error);
      toast.error("Failed to update stats");
    }
  };

  // Create new user
  const handleCreateUser = async () => {
    if (!userForm.email || !userForm.password || !userForm.player_name) {
      toast.error("Please fill in email, password, and player name");
      return;
    }
    
    setCreatingUser(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/user/create?admin_email=${adminEmail}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userForm)
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || "User created successfully!");
        setShowUserForm(false);
        setUserForm({
          email: "",
          password: "",
          player_name: "",
          initial_stars: 0,
          initial_coins: 0,
          is_premium_member: false,
          battle_pass_premium: false
        });
        fetchData(adminEmail);
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to create user");
      }
    } catch (error) {
      console.error("Create user failed:", error);
      toast.error("Failed to create user");
    } finally {
      setCreatingUser(false);
    }
  };

  // Delete user
  const handleDeleteUser = async (userId, playerName) => {
    if (!confirm(`Are you sure you want to delete user "${playerName}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/user/${userId}?admin_email=${adminEmail}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success("User deleted successfully");
        setSelectedUser(null);
        fetchData(adminEmail);
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to delete user");
      }
    } catch (error) {
      console.error("Delete user failed:", error);
      toast.error("Failed to delete user");
    }
  };

  // Toggle premium membership
  const handleToggleMembership = async (userId, enable) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/user/toggle-membership?admin_email=${adminEmail}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          user_id: userId, 
          enable: enable,
          duration_months: 1 
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        
        // Update selectedUser locally
        if (selectedUser && selectedUser.id === userId) {
          setSelectedUser(prev => ({
            ...prev,
            stats: {
              ...prev.stats,
              is_premium_member: enable,
              membership_expires: result.expires || null
            }
          }));
        }
        
        fetchData(adminEmail);
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to toggle membership");
      }
    } catch (error) {
      console.error("Toggle membership failed:", error);
      toast.error("Failed to toggle membership");
    }
  };

  // Update username
  const handleUpdateUsername = async (userId, newName) => {
    if (!newName || newName.trim().length < 2) {
      toast.error("Username must be at least 2 characters");
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/user/update-username?admin_email=${adminEmail}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, new_name: newName.trim() })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || "Username updated!");
        
        // Update selectedUser locally
        if (selectedUser && selectedUser.id === userId) {
          setSelectedUser(prev => ({
            ...prev,
            player_name: newName.trim()
          }));
        }
        
        fetchData(adminEmail);
        setEditField(null);
        setEditValue("");
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to update username");
      }
    } catch (error) {
      console.error("Update username failed:", error);
      toast.error("Failed to update username");
    }
  };

  // Filter users
  const filteredUsers = users.filter(user => 
    user.player_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Login screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 bg-slate-800/50 border-purple-500/30">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4">
              <Settings className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
            <p className="text-gray-400 text-sm mt-2">Fruit Crush Management</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="text-sm text-gray-400">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            
            {loginError && (
              <p className="text-red-400 text-sm text-center">{loginError}</p>
            )}
            
            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              disabled={loading}
            >
              {loading ? "Logging in..." : "Login"}
            </Button>
          </form>

          <Button 
            variant="ghost" 
            className="w-full mt-4 text-gray-400"
            onClick={() => navigate("/")}
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Game
          </Button>
        </Card>
      </div>
    );
  }

  // Admin dashboard
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      {/* Header */}
      <header className="bg-slate-800/80 border-b border-white/10 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Admin Panel</h1>
              <p className="text-gray-400 text-xs">{adminEmail}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => fetchData(adminEmail)}
              className="text-gray-400"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button 
              size="sm"
              onClick={handleSync}
              disabled={syncing}
              className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
            >
              {syncing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" /> Sync to Leaderboard
                </>
              )}
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate("/")}
              className="text-gray-400"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Game
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4">
        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="p-4 bg-slate-800/50 border-blue-500/30">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-blue-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_users}</p>
                  <p className="text-xs text-gray-400">Total Users</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-slate-800/50 border-yellow-500/30">
              <div className="flex items-center gap-3">
                <Star className="w-8 h-8 text-yellow-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_stars_in_game?.toLocaleString()}</p>
                  <p className="text-xs text-gray-400">Total Stars</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-slate-800/50 border-purple-500/30">
              <div className="flex items-center gap-3">
                <Crown className="w-8 h-8 text-purple-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.battle_pass_subscribers}</p>
                  <p className="text-xs text-gray-400">Battle Pass (€{stats.battle_pass_price?.price_eur}/mo)</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-slate-800/50 border-red-500/30">
              <div className="flex items-center gap-3">
                <Ban className="w-8 h-8 text-red-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.banned_users}</p>
                  <p className="text-xs text-gray-400">Banned Users</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Sync Status Card */}
        {syncStatus && (
          <Card className="p-4 bg-slate-800/50 border-green-500/30 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <RefreshCw className={`w-6 h-6 ${syncStatus.sync_needed ? 'text-yellow-400' : 'text-green-400'}`} />
                <div>
                  <p className="text-white font-medium">Sync Status</p>
                  <p className="text-xs text-gray-400">
                    {syncStatus.leaderboard_entries} / {syncStatus.total_users} users in leaderboard
                    {syncStatus.last_sync && ` • Last sync: ${new Date(syncStatus.last_sync).toLocaleString()}`}
                  </p>
                </div>
              </div>
              {syncStatus.sync_needed && (
                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                  Sync Recommended
                </Badge>
              )}
              {lastSyncResult && lastSyncResult.success && (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                  {lastSyncResult.leaderboard_synced} synced • {lastSyncResult.rankings_calculated} ranked
                </Badge>
              )}
            </div>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <Button 
            variant={activeTab === "users" ? "default" : "outline"}
            onClick={() => setActiveTab("users")}
            className={activeTab === "users" ? "bg-purple-500" : ""}
          >
            <Users className="w-4 h-4 mr-2" /> Users
          </Button>
          <Button 
            variant={activeTab === "stats" ? "default" : "outline"}
            onClick={() => setActiveTab("stats")}
            className={activeTab === "stats" ? "bg-purple-500" : ""}
          >
            <BarChart3 className="w-4 h-4 mr-2" /> Statistics
          </Button>
          <Button 
            variant={activeTab === "news" ? "default" : "outline"}
            onClick={() => setActiveTab("news")}
            className={activeTab === "news" ? "bg-purple-500" : ""}
          >
            <Newspaper className="w-4 h-4 mr-2" /> News
          </Button>
        </div>

        {/* Users Tab */}
        {activeTab === "users" && (
          <Card className="bg-slate-800/50 border-white/10">
            {/* Search and Add User */}
            <div className="p-4 border-b border-white/10">
              <div className="flex gap-3 items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <Button
                  onClick={() => setShowUserForm(true)}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="add-user-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add User
                </Button>
              </div>
            </div>

            {/* Users Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-700/50">
                  <tr>
                    <th className="text-left p-4 text-gray-400 text-sm font-medium">Player</th>
                    <th className="text-left p-4 text-gray-400 text-sm font-medium">Stars</th>
                    <th className="text-left p-4 text-gray-400 text-sm font-medium">Coins</th>
                    <th className="text-left p-4 text-gray-400 text-sm font-medium">XP</th>
                    <th className="text-left p-4 text-gray-400 text-sm font-medium">Level</th>
                    <th className="text-left p-4 text-gray-400 text-sm font-medium">Battle Pass</th>
                    <th className="text-left p-4 text-gray-400 text-sm font-medium">Status</th>
                    <th className="text-left p-4 text-gray-400 text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="border-t border-white/5 hover:bg-slate-700/30">
                      <td className="p-4">
                        <div>
                          <p className="text-white font-medium">{user.player_name || "Unknown"}</p>
                          <p className="text-gray-500 text-xs">{user.email}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-yellow-400">⭐ {user.stats?.stars?.toLocaleString() || 0}</span>
                      </td>
                      <td className="p-4">
                        <span className="text-amber-400">🪙 {user.stats?.coins?.toLocaleString() || 0}</span>
                      </td>
                      <td className="p-4">
                        <span className="text-purple-400">{user.stats?.total_xp?.toLocaleString() || 0}</span>
                      </td>
                      <td className="p-4">
                        <span className="text-white">{user.stats?.current_level || 1}</span>
                      </td>
                      <td className="p-4">
                        {user.stats?.has_battle_pass ? (
                          <Badge className="bg-gradient-to-r from-purple-500 to-pink-500">Premium</Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-400">Free</Badge>
                        )}
                      </td>
                      <td className="p-4">
                        {user.is_banned ? (
                          <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Banned</Badge>
                        ) : (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Active</Badge>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => setSelectedUser(user)}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {user.is_banned ? (
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleBanUser(user.id, false)}
                              className="text-green-400 hover:text-green-300"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleBanUser(user.id, true)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Stats Tab */}
        {activeTab === "stats" && stats && (
          <div className="grid gap-4">
            <Card className="p-6 bg-slate-800/50 border-white/10">
              <h3 className="text-lg font-bold text-white mb-4">Game Overview</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-700/50 rounded-xl">
                  <p className="text-gray-400 text-sm">Active Users</p>
                  <p className="text-2xl font-bold text-green-400">{stats.active_users}</p>
                </div>
                <div className="p-4 bg-slate-700/50 rounded-xl">
                  <p className="text-gray-400 text-sm">Avg. Levels Completed</p>
                  <p className="text-2xl font-bold text-blue-400">{stats.avg_levels_completed}</p>
                </div>
                <div className="p-4 bg-slate-700/50 rounded-xl">
                  <p className="text-gray-400 text-sm">Total XP Earned</p>
                  <p className="text-2xl font-bold text-purple-400">{stats.total_xp_earned?.toLocaleString()}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-slate-800/50 border-white/10">
              <h3 className="text-lg font-bold text-white mb-4">Battle Pass Pricing</h3>
              <div className="p-4 bg-slate-700/50 rounded-xl">
                <p className="text-gray-400 text-sm">Monthly Subscription</p>
                <p className="text-3xl font-bold text-purple-400">€{stats.battle_pass_price?.price_eur} <span className="text-sm text-gray-400">/month</span></p>
              </div>
            </Card>
          </div>
        )}

        {/* News Tab */}
        {activeTab === "news" && (
          <Card className="bg-slate-800/50 border-white/10">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">News Management</h3>
              <Button 
                onClick={() => {
                  setEditingNews(null);
                  setNewsForm({ title: "", content: "", category: "announcement", is_published: true });
                  setShowNewsForm(true);
                }}
                className="bg-green-600 hover:bg-green-700"
              >
                <Plus className="w-4 h-4 mr-2" /> Add News
              </Button>
            </div>
            
            {/* News Form Modal */}
            {showNewsForm && (
              <div className="p-4 border-b border-white/10 bg-slate-700/30">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Title</label>
                    <Input
                      value={newsForm.title}
                      onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })}
                      placeholder="News title..."
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Content</label>
                    <textarea
                      value={newsForm.content}
                      onChange={(e) => setNewsForm({ ...newsForm, content: e.target.value })}
                      placeholder="News content..."
                      rows={4}
                      className="w-full bg-slate-700 border border-slate-600 text-white rounded-md p-2 resize-none"
                    />
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="text-sm text-gray-400 mb-1 block">Category</label>
                      <select
                        value={newsForm.category}
                        onChange={(e) => setNewsForm({ ...newsForm, category: e.target.value })}
                        className="w-full bg-slate-700 border border-slate-600 text-white rounded-md p-2"
                      >
                        <option value="announcement">Announcement</option>
                        <option value="update">Update</option>
                        <option value="event">Event</option>
                      </select>
                    </div>
                    <div className="flex items-end gap-2">
                      <label className="flex items-center gap-2 text-white cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newsForm.is_published}
                          onChange={(e) => setNewsForm({ ...newsForm, is_published: e.target.checked })}
                          className="w-4 h-4"
                        />
                        Published
                      </label>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSaveNews} className="bg-blue-600 hover:bg-blue-700">
                      <Save className="w-4 h-4 mr-2" /> {editingNews ? "Update" : "Create"}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => { setShowNewsForm(false); setEditingNews(null); }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* News List */}
            <ScrollArea className="h-[400px]">
              {news.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  <Newspaper className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No news articles yet</p>
                  <p className="text-sm">Click "Add News" to create your first article</p>
                </div>
              ) : (
                <div className="divide-y divide-white/10">
                  {news.map((item) => (
                    <div key={item.id} className="p-4 hover:bg-slate-700/30 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-white font-bold truncate">{item.title}</h4>
                            <Badge className={`text-xs ${
                              item.category === 'update' ? 'bg-blue-500/20 text-blue-400' :
                              item.category === 'event' ? 'bg-green-500/20 text-green-400' :
                              'bg-purple-500/20 text-purple-400'
                            }`}>
                              {item.category}
                            </Badge>
                            {!item.is_published && (
                              <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">Draft</Badge>
                            )}
                          </div>
                          <p className="text-gray-400 text-sm line-clamp-2">{item.content}</p>
                          <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                            <Calendar className="w-3 h-3" />
                            {new Date(item.created_at).toLocaleDateString()}
                            {item.author && <span>by {item.author}</span>}
                          </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => handleEditNews(item)}
                            className="h-8"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => handleDeleteNews(item.id)}
                            className="h-8 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </Card>
        )}
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <div 
          className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
          onClick={(e) => {
            // Close when clicking the backdrop
            if (e.target === e.currentTarget) {
              setSelectedUser(null);
              setEditField(null);
            }
          }}
        >
          <Card className="w-full max-w-2xl bg-slate-800 border-purple-500/30 max-h-[85vh] flex flex-col">
            <div className="p-4 border-b border-white/10 flex items-center justify-between flex-shrink-0">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Edit className="w-5 h-5" /> Edit User
              </h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => { setSelectedUser(null); setEditField(null); }}
                className="text-white hover:bg-white/20 h-8 w-8 p-0"
                data-testid="close-edit-modal"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <div className="p-4 space-y-4">
                {/* User Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-400 text-sm">Player Name</p>
                    {editField === "player_name" ? (
                      <div className="flex gap-2 mt-1">
                        <Input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="h-8 bg-slate-600 border-slate-500 text-white"
                          data-testid="edit-player-name-input"
                        />
                        <Button 
                          size="sm" 
                          onClick={() => handleUpdateUsername(selectedUser.id, editValue)}
                          className="h-8 bg-green-500"
                        >
                          <Save className="w-3 h-3" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => { setEditField(null); setEditValue(""); }}
                          className="h-8"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <p className="text-white text-lg font-bold">{selectedUser.player_name || "Unknown"}</p>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => { setEditField("player_name"); setEditValue(selectedUser.player_name || ""); }}
                          className="h-6 px-2 text-blue-400"
                          data-testid="edit-player-name-btn"
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Email</p>
                    <p className="text-white">{selectedUser.email}</p>
                  </div>
                </div>
                
                {/* Currency Section */}
                <div className="bg-slate-700/30 rounded-lg p-4">
                  <h4 className="text-sm font-bold text-yellow-400 mb-3 flex items-center gap-2">
                    <Coins className="w-4 h-4" /> Currency
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Stars */}
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-gray-400 text-xs flex items-center gap-1">
                        <Star className="w-3 h-3 text-yellow-400" /> Stars
                      </p>
                      {editField === "stars" ? (
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="h-8 bg-slate-600 border-slate-500 text-white"
                          />
                          <Button 
                            size="sm" 
                            onClick={() => handleAdjustStats(selectedUser.id, "stars", editValue)}
                            className="h-8 bg-green-500"
                          >
                            <Save className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <p className="text-yellow-400 font-bold text-lg">⭐ {selectedUser.stats?.stars?.toLocaleString() || 0}</p>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => { setEditField("stars"); setEditValue(String(selectedUser.stats?.stars || 0)); }}
                            className="h-6 px-2 text-blue-400"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {/* Coins */}
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-gray-400 text-xs flex items-center gap-1">
                        <Coins className="w-3 h-3 text-amber-400" /> Coins
                      </p>
                      {editField === "coins" ? (
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="h-8 bg-slate-600 border-slate-500 text-white"
                          />
                          <Button 
                            size="sm" 
                            onClick={() => handleAdjustStats(selectedUser.id, "coins", editValue)}
                            className="h-8 bg-green-500"
                          >
                            <Save className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <p className="text-amber-400 font-bold text-lg">🪙 {selectedUser.stats?.coins?.toLocaleString() || 0}</p>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => { setEditField("coins"); setEditValue(String(selectedUser.stats?.coins || 0)); }}
                            className="h-6 px-2 text-blue-400"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Progress Section */}
                <div className="bg-slate-700/30 rounded-lg p-4">
                  <h4 className="text-sm font-bold text-purple-400 mb-3 flex items-center gap-2">
                    <Trophy className="w-4 h-4" /> Progress
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Total XP */}
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-gray-400 text-xs flex items-center gap-1">
                        <Zap className="w-3 h-3 text-purple-400" /> Total XP
                      </p>
                      {editField === "total_xp" ? (
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="h-8 bg-slate-600 border-slate-500 text-white"
                          />
                          <Button 
                            size="sm" 
                            onClick={() => handleAdjustStats(selectedUser.id, "total_xp", editValue)}
                            className="h-8 bg-green-500"
                          >
                            <Save className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <p className="text-purple-400 font-bold">{selectedUser.stats?.total_xp?.toLocaleString() || 0}</p>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => { setEditField("total_xp"); setEditValue(String(selectedUser.stats?.total_xp || 0)); }}
                            className="h-6 px-2 text-blue-400"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {/* Prestige */}
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-gray-400 text-xs flex items-center gap-1">
                        <Crown className="w-3 h-3 text-pink-400" /> Prestige
                      </p>
                      {editField === "prestige" ? (
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="h-8 bg-slate-600 border-slate-500 text-white"
                          />
                          <Button 
                            size="sm" 
                            onClick={() => handleAdjustStats(selectedUser.id, "prestige", editValue)}
                            className="h-8 bg-green-500"
                          >
                            <Save className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <p className="text-pink-400 font-bold">{selectedUser.stats?.prestige || 0}</p>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => { setEditField("prestige"); setEditValue(String(selectedUser.stats?.prestige || 0)); }}
                            className="h-6 px-2 text-blue-400"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {/* Current Level */}
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-gray-400 text-xs">Current Level</p>
                      {editField === "current_level" ? (
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="h-8 bg-slate-600 border-slate-500 text-white"
                          />
                          <Button 
                            size="sm" 
                            onClick={() => handleAdjustStats(selectedUser.id, "current_level", editValue)}
                            className="h-8 bg-green-500"
                          >
                            <Save className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <p className="text-white font-bold">{selectedUser.stats?.current_level || 1}</p>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => { setEditField("current_level"); setEditValue(String(selectedUser.stats?.current_level || 1)); }}
                            className="h-6 px-2 text-blue-400"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {/* Completed Levels (read-only) */}
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-gray-400 text-xs">Completed Levels</p>
                      <p className="text-white font-bold">{selectedUser.stats?.completed_levels || 0}</p>
                    </div>
                  </div>
                </div>
                
                {/* Health Section */}
                <div className="bg-slate-700/30 rounded-lg p-4">
                  <h4 className="text-sm font-bold text-red-400 mb-3 flex items-center gap-2">
                    <Heart className="w-4 h-4" /> Health
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Current HP */}
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-gray-400 text-xs">Current HP</p>
                      {editField === "current_hp" ? (
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="h-8 bg-slate-600 border-slate-500 text-white"
                          />
                          <Button 
                            size="sm" 
                            onClick={() => handleAdjustStats(selectedUser.id, "current_hp", editValue)}
                            className="h-8 bg-green-500"
                          >
                            <Save className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <p className="text-red-400 font-bold">❤️ {selectedUser.stats?.current_hp || 100}</p>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => { setEditField("current_hp"); setEditValue(String(selectedUser.stats?.current_hp || 100)); }}
                            className="h-6 px-2 text-blue-400"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {/* Max HP */}
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-gray-400 text-xs">Max HP</p>
                      {editField === "max_hp" ? (
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="h-8 bg-slate-600 border-slate-500 text-white"
                          />
                          <Button 
                            size="sm" 
                            onClick={() => handleAdjustStats(selectedUser.id, "max_hp", editValue)}
                            className="h-8 bg-green-500"
                          >
                            <Save className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <p className="text-pink-400 font-bold">💖 {selectedUser.stats?.max_hp || 100}</p>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => { setEditField("max_hp"); setEditValue(String(selectedUser.stats?.max_hp || 100)); }}
                            className="h-6 px-2 text-blue-400"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Battle Pass Section */}
                <div className="bg-slate-700/30 rounded-lg p-4">
                  <h4 className="text-sm font-bold text-purple-400 mb-3 flex items-center gap-2">
                    <Crown className="w-4 h-4" /> Battle Pass
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-gray-400 text-xs">Premium Status</p>
                      <div className="flex items-center justify-between mt-1">
                        {selectedUser.stats?.has_battle_pass ? (
                          <Badge className="bg-gradient-to-r from-purple-500 to-pink-500">Premium Active</Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-400">Free User</Badge>
                        )}
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleAdjustStats(selectedUser.id, "battle_pass_premium", selectedUser.stats?.has_battle_pass ? 0 : 1)}
                          className="h-6 px-2 text-blue-400"
                        >
                          {selectedUser.stats?.has_battle_pass ? "Remove" : "Grant"}
                        </Button>
                      </div>
                    </div>
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-gray-400 text-xs">Current Tier</p>
                      <p className="text-purple-400 font-bold">{selectedUser.stats?.battle_pass_tier || 0} / 50</p>
                    </div>
                  </div>
                </div>
                
                {/* Premium Membership Section */}
                <div className="bg-gradient-to-r from-amber-900/30 to-orange-900/30 rounded-lg p-4 border border-amber-500/30">
                  <h4 className="text-sm font-bold text-amber-400 mb-3 flex items-center gap-2">
                    <Crown className="w-4 h-4" /> Premium Membership (VIP)
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-gray-400 text-xs">Membership Status</p>
                      <div className="flex items-center justify-between mt-1">
                        {selectedUser.stats?.is_premium_member ? (
                          <Badge className="bg-gradient-to-r from-amber-500 to-orange-500">VIP Active</Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-400">Not a Member</Badge>
                        )}
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleToggleMembership(selectedUser.id, !selectedUser.stats?.is_premium_member)}
                          className={`h-6 px-2 ${selectedUser.stats?.is_premium_member ? 'text-red-400 hover:text-red-300' : 'text-amber-400 hover:text-amber-300'}`}
                          data-testid="toggle-membership-btn"
                        >
                          {selectedUser.stats?.is_premium_member ? "Remove VIP" : "Grant VIP"}
                        </Button>
                      </div>
                    </div>
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-gray-400 text-xs">Expires</p>
                      <p className="text-amber-400 font-bold text-sm">
                        {selectedUser.stats?.membership_expires 
                          ? new Date(selectedUser.stats.membership_expires).toLocaleDateString()
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                  {selectedUser.stats?.is_premium_member && (
                    <div className="mt-3 p-2 bg-amber-500/10 rounded-lg text-xs text-amber-300">
                      <span className="font-bold">VIP Benefits:</span> +500 Health, 1.2x XP, 2x Coins, Crystal Paradise + Royal Citadel access
                    </div>
                  )}
                </div>

                {/* Ban Actions */}
                <div className="flex gap-2 pt-4 border-t border-white/10">
                  {selectedUser.is_banned ? (
                    <Button 
                      className="flex-1 bg-green-500 hover:bg-green-600"
                      onClick={() => handleBanUser(selectedUser.id, false)}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" /> Unban User
                    </Button>
                  ) : (
                    <Button 
                      className="flex-1 bg-red-500 hover:bg-red-600"
                      onClick={() => handleBanUser(selectedUser.id, true)}
                    >
                      <Ban className="w-4 h-4 mr-2" /> Ban User
                    </Button>
                  )}
                </div>
                
                {/* Delete User */}
                {!selectedUser.is_admin && (
                  <div className="pt-4 border-t border-red-500/30">
                    <Button 
                      className="w-full bg-red-600/20 hover:bg-red-600/40 text-red-400 border border-red-500/30"
                      onClick={() => handleDeleteUser(selectedUser.id, selectedUser.player_name)}
                      data-testid="delete-user-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Delete User Permanently
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}
      
      {/* User Creation Modal */}
      {showUserForm && (
        <div 
          className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowUserForm(false);
            }
          }}
        >
          <Card className="w-full max-w-lg bg-slate-800 border-green-500/30" data-testid="create-user-modal">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Plus className="w-5 h-5 text-green-400" /> Create New User
              </h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowUserForm(false)}
                className="text-white hover:bg-white/20 h-8 w-8 p-0"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Required Fields */}
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Email *</label>
                  <Input
                    type="email"
                    placeholder="user@example.com"
                    value={userForm.email}
                    onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="create-user-email"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Password *</label>
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={userForm.password}
                    onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="create-user-password"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Player Name *</label>
                  <Input
                    type="text"
                    placeholder="FruitMaster123"
                    value={userForm.player_name}
                    onChange={(e) => setUserForm(prev => ({ ...prev, player_name: e.target.value }))}
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="create-user-name"
                  />
                </div>
              </div>
              
              {/* Initial Resources */}
              <div className="bg-slate-700/30 rounded-lg p-3">
                <h4 className="text-sm font-bold text-yellow-400 mb-3">Initial Resources</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Stars ⭐</label>
                    <Input
                      type="number"
                      min="0"
                      value={userForm.initial_stars}
                      onChange={(e) => setUserForm(prev => ({ ...prev, initial_stars: parseInt(e.target.value) || 0 }))}
                      className="bg-slate-600 border-slate-500 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Coins 🪙</label>
                    <Input
                      type="number"
                      min="0"
                      value={userForm.initial_coins}
                      onChange={(e) => setUserForm(prev => ({ ...prev, initial_coins: parseInt(e.target.value) || 0 }))}
                      className="bg-slate-600 border-slate-500 text-white"
                    />
                  </div>
                </div>
              </div>
              
              {/* Premium Options */}
              <div className="bg-slate-700/30 rounded-lg p-3">
                <h4 className="text-sm font-bold text-purple-400 mb-3">Premium Status</h4>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-slate-600/30">
                    <input
                      type="checkbox"
                      checked={userForm.battle_pass_premium}
                      onChange={(e) => setUserForm(prev => ({ ...prev, battle_pass_premium: e.target.checked }))}
                      className="w-4 h-4 rounded border-slate-500 bg-slate-600 text-purple-500 focus:ring-purple-500"
                    />
                    <div>
                      <span className="text-white text-sm font-medium">Premium Battle Pass</span>
                      <p className="text-xs text-gray-400">Access to all Battle Pass rewards</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-slate-600/30">
                    <input
                      type="checkbox"
                      checked={userForm.is_premium_member}
                      onChange={(e) => setUserForm(prev => ({ ...prev, is_premium_member: e.target.checked }))}
                      className="w-4 h-4 rounded border-slate-500 bg-slate-600 text-amber-500 focus:ring-amber-500"
                    />
                    <div>
                      <span className="text-white text-sm font-medium">Premium Membership</span>
                      <p className="text-xs text-gray-400">Full membership benefits + 500 bonus stars</p>
                    </div>
                  </label>
                </div>
              </div>
              
              {/* Submit Button */}
              <Button
                onClick={handleCreateUser}
                disabled={creatingUser}
                className="w-full bg-green-600 hover:bg-green-700 h-11"
                data-testid="submit-create-user"
              >
                {creatingUser ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
                ) : (
                  <><Plus className="w-4 h-4 mr-2" /> Create User</>
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
